import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const empty = () => ({ op: 'RECEIVE', productId: '', supplierId: '', warehouseId: '', quantity: '', unitPrice: '', note: '' });

export function ConsignmentOpModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  const products = useQuery({ queryKey: ['inventory', 'products'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });
  const suppliers = useQuery({ queryKey: ['inventory', 'suppliers'], queryFn: () => inventoryApi.listSuppliers().then((r) => r.items), enabled: open });
  const warehouses = useQuery({ queryKey: ['inventory', 'warehouses'], queryFn: () => inventoryApi.listWarehouses().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: ({ op, body }) => (op === 'RECEIVE' ? inventoryApi.receiveConsignment(body) : inventoryApi.withdrawConsignment(body)),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory', 'consignment'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.productId || !form.supplierId || !form.warehouseId || !form.quantity || Number(form.quantity) <= 0) { setError(t('cons.error.required')); return; }
    const base = { productId: form.productId, supplierId: form.supplierId, warehouseId: form.warehouseId, quantity: Number(form.quantity) };
    const body = form.op === 'RECEIVE' ? { ...base, unitPrice: Number(form.unitPrice) || 0 } : { ...base, note: form.note || null };
    save.mutate({ op: form.op, body });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('cons.op.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('cons.op.type')}</span>
          <select className="input" value={form.op} onChange={set('op')}>
            <option value="RECEIVE">{t('cons.op.receive')}</option>
            <option value="WITHDRAW">{t('cons.op.withdraw')}</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('cons.col.product')}</span>
          <select className="input" value={form.productId} onChange={set('productId')} required>
            <option value="">-</option>
            {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('cons.col.supplier')}</span>
            <select className="input" value={form.supplierId} onChange={set('supplierId')} required>
              <option value="">-</option>
              {(suppliers.data || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('cons.col.warehouse')}</span>
            <select className="input" value={form.warehouseId} onChange={set('warehouseId')} required>
              <option value="">-</option>
              {(warehouses.data || []).map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
            </select>
          </label>
        </div>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 140 }}>
            <span className="field-label">{t('cons.col.quantity')}</span>
            <input className="input num" type="number" min="0.0001" step="any" value={form.quantity} onChange={set('quantity')} required />
          </label>
          {form.op === 'RECEIVE' ? (
            <label className="field" style={{ maxWidth: 160 }}>
              <span className="field-label">{t('cons.col.price')}</span>
              <input className="input num" type="number" min="0" step="0.01" value={form.unitPrice} onChange={set('unitPrice')} />
            </label>
          ) : (
            <label className="field">
              <span className="field-label">{t('cons.field.note')}</span>
              <input className="input" value={form.note} onChange={set('note')} />
            </label>
          )}
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
